/**
 * Shared constants and utilities for identifying and working with focusable elements.
 * Used across focus management, skip links, and other accessibility features.
 *
 * NOTE: To add support for custom focusable elements:
 * - Add the CSS selector to FOCUSABLE_SELECTOR (e.g., '[role="custom"]', '[data-focusable]')
 */

/**
 * Common selector for focusable elements
 * Includes: links, buttons, inputs, form controls, elements with tabindex, and ARIA interactive roles
 */
export const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"]):not([aria-disabled="true"])',
  'input:not([disabled]):not([tabindex="-1"]):not([aria-disabled="true"])',
  'select:not([disabled]):not([tabindex="-1"]):not([aria-disabled="true"])',
  'textarea:not([disabled]):not([tabindex="-1"]):not([aria-disabled="true"])',
  '[tabindex]:not([tabindex="-1"]):not([aria-disabled="true"])',
  // Note: ARIA roles like role="button" on non-interactive elements (span, div)
  // are already covered by [tabindex] selector above, as they need tabindex to be focusable
].join(', ');

/**
 * Checks if an element is visible (not hidden by CSS)
 * Exported for use in other focus management utilities
 */
export function isElementVisible(element: HTMLElement): boolean {
  // Check computed styles - reliable in both browser and JSDOM
  const style = window.getComputedStyle(element);

  // Definitely hidden
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  // Check for opacity: 0 (visually hidden but still in layout)
  if (style.opacity === '0') {
    return false;
  }

  return true;
}

/**
 * Resolves an element to an actually focusable element.
 * If element has negative tabIndex, checks for focusable children.
 * (e.g., <span role="button"><button>...</button></span> → returns <button>)
 */
const resolveFocusableElement = (element: HTMLElement): HTMLElement | null => {
  if (element.tabIndex >= 0) {
    return element;
  }

  // Element can't be focused - check for focusable children
  const focusableChild = element.querySelector(FOCUSABLE_SELECTOR) as HTMLElement;

  // Recursively resolve in case child is also a wrapper
  return focusableChild ? resolveFocusableElement(focusableChild) : null;
};

/**
 * Finds the next focusable element after a given element in document order
 * @param afterElement - The element to start searching after
 * @param scope - Optional container to limit search scope (defaults to document)
 * @returns The next focusable element or null if none found
 */
export const findNextFocusableElement = (
  afterElement: HTMLElement,
  scope: HTMLElement | Document = document,
): HTMLElement | null => {
  // Get all focusable elements in the scope
  const allFocusables = Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

  // Find the index of the current element
  const currentIndex = allFocusables.indexOf(afterElement);

  if (currentIndex !== -1) {
    // Element is in the list, find next visible one and resolve it
    for (let i = currentIndex + 1; i < allFocusables.length; i++) {
      if (isElementVisible(allFocusables[i])) {
        return resolveFocusableElement(allFocusables[i]);
      }
    }
    return null;
  }

  // Element is not focusable (e.g., tabindex="-1" marker)
  // Use compareDocumentPosition for efficient DOM position comparison
  // compareDocumentPosition is much faster than building arrays and using indexOf
  // DOCUMENT_POSITION_FOLLOWING (4) means the other node comes after this node
  for (const focusable of allFocusables) {
    // Skip if focusable is a descendant of afterElement (they're "inside", not "after")
    if (afterElement.contains(focusable)) {
      continue;
    }

    // Check if focusable comes after afterElement in document order
    const position = afterElement.compareDocumentPosition(focusable);
    const isAfter = (position & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;

    if (!isAfter) {
      continue; // Skip elements before afterElement
    }

    // Now check visibility
    if (isElementVisible(focusable)) {
      return focusable;
    }
  }

  return null;
};

/**
 * Finds the previous focusable element before a given element in document order
 * @param beforeElement - The element to start searching before
 * @param scope - Optional container to limit search scope (defaults to document)
 * @returns The previous focusable element or null if none found
 */
export const findPreviousFocusableElement = (
  beforeElement: HTMLElement,
  scope: HTMLElement | Document = document,
): HTMLElement | null => {
  // Get all focusable elements in the scope
  const allFocusables = Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

  // Find the index of the current element
  const currentIndex = allFocusables.indexOf(beforeElement);

  // Return the previous one, resolving it to an actually focusable element
  if (currentIndex !== -1 && currentIndex > 0) {
    return resolveFocusableElement(allFocusables[currentIndex - 1]);
  }

  return null;
};

//Gets all focusable elements within a container
export const getAllFocusableElements = (container: HTMLElement | Document = document): HTMLElement[] => {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
};
